import React, { useState } from "react";

import { AlertCircle } from "lucide-react";
import { BadgePercent } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { TableSkeleton } from "~/components/ui/skeletons";
import LatePaymentsTable from "~/pages/payments/components/LatePaymentsTable";
import PaymentDetailsDrawer from "~/pages/payments/components/PaymentDetailsDrawer";
import { useLatePayments } from "~/pages/payments/hooks/useLatePayments";
import { formatCurrency } from "~/utils/helpers";

const LatePaymentManagementPage = () => {
  const {
    latePayments,
    isLoadingLatePayments,
    totalLatePayments,
    mapSeverity,
  } = useLatePayments();

  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null
  );

  // Group payments by their lateness category
  const lowLateness =
    latePayments?.filter((payment) => payment.daysLate < 30) || [];
  const mediumLateness =
    latePayments?.filter(
      (payment) => payment.daysLate >= 30 && payment.daysLate < 60
    ) || [];
  const highLateness =
    latePayments?.filter((payment) => payment.daysLate >= 60) || [];

  // Calculate totals for each category
  const lowLatenessTotal = lowLateness.reduce(
    (sum, payment) => sum + payment.remaining_amount,
    0
  );
  const mediumLatenessTotal = mediumLateness.reduce(
    (sum, payment) => sum + payment.remaining_amount,
    0
  );
  const highLatenessTotal = highLateness.reduce(
    (sum, payment) => sum + payment.remaining_amount,
    0
  );

  // Calculate total proportional interest to collect
  const totalInterestToCollect =
    latePayments?.reduce(
      (sum, payment) => sum + (Number(payment.proportional_interest) || 0),
      0
    ) || 0;

  if (isLoadingLatePayments) {
    return (
      <div className="container mx-auto py-6">
        <h1 className="mb-6 text-2xl font-bold">Pagos Atrasados</h1>

        <div className="mb-6 flex flex-col space-y-4 md:flex-row md:space-x-6 md:space-y-0">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i} className="flex-1">
              <CardHeader className="pb-2">
                <Skeleton className="h-6 w-56" />
                <Skeleton className="mt-2 h-4 w-72 max-w-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="mb-2 h-9 w-40" />
                <Skeleton className="h-4 w-48" />
              </CardContent>
            </Card>
          ))}
        </div>

        <TableSkeleton rows={8} columns={6} />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <h1 className="mb-6 text-2xl font-bold">Pagos Atrasados</h1>

      <div className="mb-6 flex flex-col space-y-4 md:flex-row md:space-x-6 md:space-y-0">
        {/* Pagos Atrasados Summary Card */}
        <Card className="flex-1 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-lg font-medium">
              <AlertCircle className="mr-2 h-5 w-5 text-red-500" />
              Resumen de Pagos Atrasados
            </CardTitle>
            <CardDescription>
              Pagos vencidos que requieren atención inmediata
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-2 text-3xl font-bold text-red-600">
              {formatCurrency(totalLatePayments)}
            </div>
            <div className="text-sm text-red-500">
              {latePayments?.length || 0} pagos atrasados en total
            </div>
          </CardContent>
        </Card>

        {/* Intereses por Recaudar Card */}
        <Card className="flex-1 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-lg font-medium">
              <BadgePercent className="mr-2 h-5 w-5 text-amber-500" />
              Intereses por recaudar
            </CardTitle>
            <CardDescription>
              Suma total de intereses generados (por atraso)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-2 text-3xl font-bold text-amber-600">
              {formatCurrency(totalInterestToCollect)}
            </div>
            <div className="text-sm text-amber-500">
              Calculado sobre los pagos con atraso
            </div>
          </CardContent>
        </Card>
      </div>

      <LatePaymentsTable
        latePayments={latePayments}
        totalLatePayments={totalLatePayments}
        isLoading={isLoadingLatePayments}
        getLatenessSeverity={mapSeverity}
        showFullTable={true}
      />

      {/* Payment Details Drawer */}
      <PaymentDetailsDrawer
        paymentId={selectedPaymentId}
        open={!!selectedPaymentId}
        onClose={() => setSelectedPaymentId(null)}
        payment={latePayments?.find((p) => p.id === selectedPaymentId)}
      />
    </div>
  );
};

export default LatePaymentManagementPage;
